/**
 * KanbanColumn: droppable card list + hover-revealed collapse/drag controls.
 * Collapsed = full-height pill: rotated title → count (clustered at top).
 *
 * Default header: [select] [title] [count] … [⋮]
 * Hover / touch / focus-within: … [collapse] [drag] [⋮]
 * Menu stays always visible; only the handle initiates column reorder.
 */

import { useDroppable } from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ChevronLeft, GripVertical } from 'lucide-react';
import { cn } from '@/utils/cn';
import { Button } from '../../atoms/Button';
import {
  getKanbanColumnClass,
  kanbanColumnBodyClass,
  kanbanColumnCollapsedClusterClass,
  kanbanColumnCollapsedTitleClass,
  kanbanColumnCountClass,
  kanbanColumnCountOverClass,
  kanbanColumnHeaderActionsClass,
  kanbanColumnHeaderClass,
  kanbanColumnHeaderCollapsedClass,
  kanbanColumnHeaderHoverControlsClass,
  kanbanColumnHeaderPadClass,
  kanbanColumnHeaderStartClass,
  kanbanSelectionGutterClass,
  toColumnSortId,
} from './Kanban.styles';
import type { KanbanColumnProps } from './Kanban.types';

export function KanbanColumn({
  id,
  columnId,
  header,
  count,
  wipLimit,
  itemIds,
  children,
  isHighlighted = false,
  isDropTarget = false,
  isCollapsed = false,
  enableColumnDrag = false,
  columnDragHandleLabel = 'Reorder column',
  headerStart,
  headerActions,
  droppableData,
  className,
  hideMenuInDrag = false,
  onExpand,
  onCollapse,
  canCollapse = false,
}: KanbanColumnProps) {
  const resolvedColumnId = columnId ?? id;
  const columnSortId = toColumnSortId(resolvedColumnId);
  const sortableData = {
    type: 'column' as const,
    columnId: resolvedColumnId,
    ...droppableData,
  };
  const cardDropData = {
    type: 'card-drop' as const,
    columnId: resolvedColumnId,
    ...droppableData,
  };

  const {
    attributes,
    listeners,
    setNodeRef: setSortableRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging: isColumnDragging,
  } = useSortable({
    id: columnSortId,
    data: sortableData,
    disabled: !enableColumnDrag || isCollapsed,
  });

  const { setNodeRef: setDroppableRef, isOver: isDroppableOver } = useDroppable({
    id,
    data: cardDropData,
    disabled: isCollapsed,
  });

  const setOuterRef = (node: HTMLElement | null) => {
    if (enableColumnDrag && !isCollapsed) setSortableRef(node);
  };

  const wipOver =
    count != null && wipLimit != null && count > wipLimit;
  const countLabel =
    count == null
      ? null
      : wipLimit != null
        ? `${count}/${wipLimit}`
        : String(count);

  const showHoverControls = canCollapse || enableColumnDrag;

  const countBadge =
    countLabel != null ? (
      <span
        className={
          wipOver ? kanbanColumnCountOverClass : kanbanColumnCountClass
        }
      >
        {countLabel}
      </span>
    ) : null;

  return (
    <div
      ref={setOuterRef}
      data-kanban-column-id={resolvedColumnId}
      style={
        enableColumnDrag && !isCollapsed
          ? {
              transform: CSS.Transform.toString(transform),
              transition,
            }
          : undefined
      }
      className={cn(
        getKanbanColumnClass({
          isDropTarget: isDropTarget || isDroppableOver,
          isHighlighted,
          isColumnDragging,
          isCollapsed,
        }),
        wipOver && 'ring-1 ring-red/30',
        isColumnDragging && 'cursor-grabbing',
        className
      )}
    >
      {isCollapsed ? (
        <button
          type="button"
          className={cn(
            kanbanColumnHeaderCollapsedClass,
            'w-full border-0 bg-transparent cursor-pointer hover:bg-dark-invirtus-soft/55 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue/40 focus-visible:ring-inset rounded-full'
          )}
          onClick={() => onExpand?.()}
          aria-label="Expand column"
        >
          <div className={kanbanColumnCollapsedClusterClass}>
            <div
              className={kanbanColumnCollapsedTitleClass}
              style={{
                writingMode: 'vertical-rl',
                textOrientation: 'mixed',
              }}
            >
              {header}
            </div>
            {countBadge}
          </div>
        </button>
      ) : (
        <>
          <div
            className={cn(
              kanbanColumnHeaderClass,
              headerStart
                ? kanbanColumnHeaderPadClass
                : 'px-[var(--kanban-body-px)]'
            )}
          >
            <div className={kanbanColumnHeaderStartClass}>
              {headerStart ? (
                <div className={kanbanSelectionGutterClass}>{headerStart}</div>
              ) : null}
              <div className="min-w-0 flex items-center gap-2 flex-1">
                <div className="min-w-0 truncate text-sm font-semibold text-dark-invirtus">
                  {header}
                </div>
                {countBadge}
              </div>
            </div>

            <div className={kanbanColumnHeaderActionsClass}>
              {showHoverControls && !hideMenuInDrag ? (
                <div
                  className={cn(
                    kanbanColumnHeaderHoverControlsClass,
                    isColumnDragging &&
                      '!opacity-100 !pointer-events-auto'
                  )}
                >
                  {canCollapse ? (
                    <Button
                      type="icon"
                      intent="muted"
                      size="sm"
                      icon={<ChevronLeft />}
                      aria-label="Collapse column"
                      onPointerDown={(event) => event.stopPropagation()}
                      onClick={(event) => {
                        event.stopPropagation();
                        onCollapse?.();
                      }}
                    />
                  ) : null}
                  {enableColumnDrag ? (
                    <Button
                      ref={setActivatorNodeRef}
                      type="icon"
                      intent="muted"
                      size="sm"
                      icon={<GripVertical />}
                      aria-label={columnDragHandleLabel}
                      className="!cursor-grab active:!cursor-grabbing touch-none"
                      {...attributes}
                      {...listeners}
                    />
                  ) : null}
                </div>
              ) : null}
              {!hideMenuInDrag ? headerActions : null}
            </div>
          </div>

          <div
            ref={setDroppableRef}
            className={kanbanColumnBodyClass}
            data-kanban-flip-scope={id}
          >
            <SortableContext
              items={itemIds}
              strategy={verticalListSortingStrategy}
            >
              {children}
            </SortableContext>
          </div>
        </>
      )}
    </div>
  );
}
